// /frontend/src/pages/CustomDesignDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Layout from '../components/layout/Layout';
import Spinner from '../components/ui/Spinner';
import { toast } from 'react-toastify';
import { uploadService } from '../services/uploadService';
import { cartService } from '../services/cartService';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { addToCart } from '../features/cart/cartSlice'; // Import the cart action

// Helper function to convert base64 to blob for PDFs
const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

const CustomDesignDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  
  const [designRequest, setDesignRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revisionText, setRevisionText] = useState('');
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    const fetchDesignRequest = async () => {
      try {
        const data = await uploadService.getDesignRequest(requestId);
        setDesignRequest(data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load design request details');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchDesignRequest();
    }
  }, [requestId]);

  const handleRevisionSubmit = async (e) => {
    e.preventDefault();
    
    if (!revisionText.trim()) {
      return;
    }
    
    setSubmittingRevision(true);
    
    try {
      // Use the uploadService for submitting revisions
      const updatedRequest = await uploadService.submitRevision(requestId, revisionText);
      setDesignRequest(updatedRequest);
      setRevisionText('');
      
      toast.success('Revision request submitted successfully');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmittingRevision(false);
    }
  };

  // New function to handle design approval
  const [rejecting, setRejecting] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  const handleApproveDesign = async () => {
    setApproving(true);
    
    try {
      // Use the existing updateStatus method to set design as completed
      const updatedRequest = await uploadService.updateStatus(requestId, 'completed');
      setDesignRequest(updatedRequest);
      
      toast.success('Design approved successfully');
    } catch (err) {
      toast.error(err.message || 'Something went wrong while approving the design');
    } finally {
      setApproving(false);
    }
  };
  
  const handleRejectDesign = async () => {
    setRejecting(true);
    
    try {
      // Use the existing updateStatus method to set design as completed
      // In this case, we're marking it as completed with rejection feedback
      const updatedRequest = await uploadService.updateStatus(requestId, 'completed');
      setDesignRequest(updatedRequest);
      
      toast.success('Feedback received. Your request has been completed.');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setRejecting(false);
    }
  };
  
  const handleAddAllToCart = async () => {
    setAddingToCart(true);
    
    try {
      if (designRequest.suggestedProducts && designRequest.suggestedProducts.length > 0) {
        // Process each product sequentially with direct API calls
        for (const product of designRequest.suggestedProducts) {
          console.log('Adding product to cart:', product);
          
          // Convert variant format from design request to cart format
          // Cart format expects: { color: "light" } instead of nested options object
          const variantOptions = {};
          
          if (product.variant && product.variant.options) {
            // Extract variant info from the complex nested structure
            Object.entries(product.variant.options).forEach(([variantType, option]) => {
              // Convert to lowercase for consistency with cart format
              const variantKey = variantType.toLowerCase();
              // Use the option id as the value
              variantOptions[variantKey] = option.id;
            });
          }
          
          // Using cartService directly to add each product to the cart via API
          await cartService.addToCart(
            product.productId,
            product.quantity || 1,
            variantOptions // Simplified format: { color: "light" }
          );
          
          console.log(`Product ${product.name} added to cart`);
        }
        
        toast.success('All suggested products added to your cart');
      } else {
        toast.info('No products to add to cart');
      }
    } catch (err) {
      console.error('Failed to add products to cart:', err);
      toast.error('Failed to add products to cart: ' + (err.message || 'Unknown error'));
    } finally {
      setAddingToCart(false);
    }
  };

  const getTimelineStep = () => {
    switch (designRequest.status) {
      case 'pending':
        return 1;
      case 'in_progress':
        return 2;
      case 'review':
        return 3;
      case 'revision':
        return 3;
      case 'completed':
        return 4;
      default:
        return 1;
    }
  };

  // Function to view PDF from base64 data
  const viewPdf = (data, contentType) => {
    const blob = b64toBlob(data, contentType);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
            <p>{error}</p>
          </div>
          <Link to="/account/design-requests" className="text-teal-600 hover:text-teal-800">
            ← Back to Design Requests
          </Link>
        </div>
      </Layout>
    );
  }

  const timelineStep = getTimelineStep();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to="/account/design-requests" className="text-teal-600 hover:text-teal-800 mb-2 inline-block">
              ← Back to Design Requests
            </Link>
            <h1 className="text-3xl font-bold">Design Request Details</h1>
          </div>
          
          {(designRequest.paymentStatus === 'pending' || designRequest.paymentStatus === 'rejected') && (
            <Link 
              to={`/checkout/design-request/${designRequest._id}`}
              className="bg-teal-800 text-white px-5 py-2 rounded hover:bg-teal-900 transition-all"
            >
              Complete Payment
            </Link>
          )}
        </div>

        {/* Progress timeline */}
        <div className="mb-10">
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-between">
              <div className="flex flex-col items-center">
                <span className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  timelineStep >= 1 ? 'bg-teal-600 text-white' : 'bg-white border-2 border-gray-300 text-gray-500'
                }`}>
                  1
                </span>
                <span className="text-sm mt-2">Submitted</span>
              </div>
              
              <div className="flex flex-col items-center">
                <span className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  timelineStep >= 2 ? 'bg-teal-600 text-white' : 'bg-white border-2 border-gray-300 text-gray-500'
                }`}>
                  2
                </span>
                <span className="text-sm mt-2">In Progress</span>
              </div>
              
              <div className="flex flex-col items-center">
                <span className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  timelineStep >= 3 ? 'bg-teal-600 text-white' : 'bg-white border-2 border-gray-300 text-gray-500'
                }`}>
                  3
                </span>
                <span className="text-sm mt-2">Review</span>
              </div>
              
              <div className="flex flex-col items-center">
                <span className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  timelineStep >= 4 ? 'bg-teal-600 text-white' : 'bg-white border-2 border-gray-300 text-gray-500'
                }`}>
                  4
                </span>
                <span className="text-sm mt-2">Completed</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Design Request Info */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-xl font-semibold">Request Information</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Status</h3>
                <div className="flex items-center space-x-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    designRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    designRequest.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    designRequest.status === 'review' ? 'bg-purple-100 text-purple-800' :
                    designRequest.status === 'revision' ? 'bg-orange-100 text-orange-800' :
                    designRequest.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {designRequest.status.replace('_', ' ').charAt(0).toUpperCase() + designRequest.status.replace('_', ' ').slice(1)}
                  </span>
                </div>
                
                <h3 className="text-lg font-medium mb-4">Payment</h3>
                <div className="flex items-center space-x-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    designRequest.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 
                    designRequest.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {designRequest.paymentStatus.charAt(0).toUpperCase() + designRequest.paymentStatus.slice(1)}
                  </span>
                </div>
                
                <h3 className="text-lg font-medium mb-4">Timeline</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Requested:</span>
                    <span>{new Date(designRequest.createdAt).toLocaleDateString()}</span>
                  </div>
                  {designRequest.status === 'completed' && designRequest.completedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Completed:</span>
                      <span>{new Date(designRequest.completedAt).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-4">Floor Plan</h3>
                <div className="border rounded-lg overflow-hidden">
                  {designRequest.floorPlan && (
                    designRequest.floorPlan.contentType && designRequest.floorPlan.contentType.startsWith('image/') ? (
                      <img 
                        src={`data:${designRequest.floorPlan.contentType};base64,${designRequest.floorPlan.data}`}
                        alt="Floor Plan"
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: '300px' }}
                      />
                    ) : designRequest.floorPlan.contentType === 'application/pdf' ? (
                      <div className="p-4 text-center">
                        <button
                          onClick={() => viewPdf(designRequest.floorPlan.data, designRequest.floorPlan.contentType)}
                          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                        >
                          View Floor Plan PDF
                        </button>
                      </div>
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        Floor plan available (CAD format)
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Design Concepts */}
        {designRequest.designConcepts && designRequest.designConcepts.length > 0 && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold">Design Concepts</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {designRequest.designConcepts.map((concept, index) => (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="font-medium">Concept {index + 1}</h3>
                      <p className="text-sm text-gray-500">{new Date(concept.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="p-4">
                      {concept.contentType && concept.contentType.startsWith('image/') ? (
                        <img 
                          src={`data:${concept.contentType};base64,${concept.data}`}
                          alt={`Design Concept ${index + 1}`}
                          className="w-full h-auto object-contain rounded"
                          style={{ maxHeight: '300px' }}
                        />
                      ) : concept.contentType === 'application/pdf' ? (
                        <div className="text-center">
                          <button
                            onClick={() => viewPdf(concept.data, concept.contentType)}
                            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                          >
                            View Concept PDF
                          </button>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500">
                          Concept {index + 1} available
                        </div>
                      )}
                      
                      {concept.notes && (
                        <div className="mt-4">
                          <h4 className="font-medium text-sm mb-2">Designer Notes:</h4>
                          <p className="text-gray-700 text-sm">{concept.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Suggested Products - New Section */}
        {designRequest.suggestedProducts && designRequest.suggestedProducts.length > 0 && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold">Suggested Products</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Our design team has selected these products to complement your design.
              </p>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {designRequest.suggestedProducts.map((product, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              {product.imageUrl && (
                                <img 
                                  src={product.imageUrl} 
                                  alt={product.name} 
                                  className="w-10 h-10 object-cover rounded mr-3"
                                />
                              )}
                              <div>
                                <Link 
                                  to={`/product/${product.productId}`} 
                                  className="font-medium text-gray-900 hover:text-teal-600"
                                >
                                  {product.name}
                                </Link>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {product.variant && product.variant.options ? (
                              <div className="text-xs">
                                {Object.entries(product.variant.options).map(([type, option], idx) => (
                                  <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full mr-1 mb-1 inline-block">
                                    {type}: {option.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-xs">Standard</span>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            ${parseFloat(product.price).toFixed(2)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {product.quantity || 1}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            ${((product.price || 0) * (product.quantity || 1)).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="4" className="px-4 py-2 text-right">
                          Total:
                        </td>
                        <td className="px-4 py-2">
                          ${designRequest.suggestedProducts.reduce((total, product) => 
                            total + ((product.price || 0) * (product.quantity || 1)), 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Only show Add to Cart button if design is completed */}
              {designRequest.status === 'completed' && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleAddAllToCart}
                    disabled={addingToCart}
                    className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center"
                  >
                    {addingToCart ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      'Add All to Cart'
                    )}
                  </button>
                </div>
              )}
              
              {/* Cart View Button - Show after adding products */}
              {designRequest.status === 'completed' && (
                <div className="mt-2 flex justify-end">
                  <Link 
                    to="/cart" 
                    className="text-teal-600 hover:text-teal-800 text-sm"
                  >
                    View Cart
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Revision Requests */}
        {designRequest.revisions && designRequest.revisions.length > 0 && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold">Revision Requests</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {designRequest.revisions.map((revision, index) => (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b">
                      <div className="flex justify-between">
                        <h3 className="font-medium">Revision {index + 1}</h3>
                        <span className="text-sm text-gray-500">{new Date(revision.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-700">{revision.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Action Buttons - Design Approval & Revision */}
        {(designRequest.status === 'review' || designRequest.status === 'revision') && 
         designRequest.paymentStatus === 'paid' && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold">Review Design</h2>
            </div>
            <div className="p-6">
              {/* Show different options based on revision count */}
              {designRequest.revisionCount < 2 ? (
                <div className="flex flex-col md:flex-row md:justify-between space-y-4 md:space-y-0 md:space-x-4">
                  <div className="flex-1">
                    <h3 className="font-medium mb-2">Approve Design</h3>
                    <p className="text-gray-600 mb-4">
                      If you are satisfied with the design concept, you can approve it to complete the design process.
                    </p>
                    <button
                      onClick={handleApproveDesign}
                      disabled={approving}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 w-full"
                    >
                      {approving ? 'Processing...' : 'Approve Design'}
                    </button>
                  </div>
                  
                  <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pt-4 md:pt-0 md:pl-4">
                    <h3 className="font-medium mb-2">Request Revision</h3>
                    <p className="text-gray-600 mb-4">
                      You have used {designRequest.revisionCount} of 2 available revisions.
                    </p>
                    <form onSubmit={handleRevisionSubmit}>
                      <textarea
                        value={revisionText}
                        onChange={(e) => setRevisionText(e.target.value)}
                        rows="3"
                        className="w-full border border-gray-300 rounded p-3 mb-4"
                        placeholder="Describe the changes you would like to see in the design..."
                        required
                      ></textarea>
                      <button
                        type="submit"
                        disabled={submittingRevision}
                        className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 w-full"
                      >
                        {submittingRevision ? 'Submitting...' : 'Submit Revision Request'}
                      </button>
                    </form>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col space-y-6">
                  <div>
                    <p className="text-gray-600 mb-4">
                      You have used all your available revisions. Please finalize your decision about the design.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={handleApproveDesign}
                      disabled={approving}
                      className="flex-1 px-4 py-3 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      {approving ? 'Processing...' : 'Approve Design'}
                    </button>
                    
                    <button
                      onClick={handleRejectDesign}
                      disabled={rejecting}
                      className="flex-1 px-4 py-3 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      {rejecting ? 'Processing...' : 'Reject Design & Complete Request'}
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-500 italic">
                    Note: Both actions will mark your design request as completed. If you reject the design, we'll use your feedback to improve our future services.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Payment Evidence */}
        {designRequest.paymentEvidence && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold">Payment Evidence</h2>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  Uploaded on: {new Date(designRequest.paymentEvidenceUploadedAt).toLocaleString()}
                </p>
                <p className="text-gray-600 mb-4">
                  Status: 
                  <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${
                    designRequest.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {designRequest.paymentStatus === 'paid' ? 'Verified' : designRequest.paymentStatus}
                  </span>
                </p>
              </div>
              
              {designRequest.paymentEvidence.contentType && designRequest.paymentEvidence.contentType.startsWith('image/') ? (
                <img 
                  src={`data:${designRequest.paymentEvidence.contentType};base64,${designRequest.paymentEvidence.data}`}
                  alt="Payment Evidence"
                  className="max-w-full h-auto rounded border"
                  style={{ maxHeight: '300px' }}
                />
              ) : designRequest.paymentEvidence.contentType === 'application/pdf' ? (
                <button
                  onClick={() => viewPdf(designRequest.paymentEvidence.data, designRequest.paymentEvidence.contentType)}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  View Payment Evidence PDF
                </button>
              ) : (
                <p className="text-gray-600">Payment evidence uploaded</p>
              )}
            </div>
          </div>
        )}
        
      </div>
    </Layout>
  );
};

export default CustomDesignDetail;